import { DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { CommonDialog } from "../../../components/common/CommonDialog";
import { FormButtonGroup, FormTextInput } from "../../../components/form";
import { useOrganizationContext } from "../../../contexts/OrganizationContext";
import { useSessionContext } from "../../../contexts/SessionContext";
import {
  GetOrganizations,
  PatchOrganizationCpfToCnpj,
  Validate,
} from "../../../services/fhir";
import { HTTPResponseStatus } from "../../../utils/enum";
import { useFormatter } from "../../../utils/useFormatter";
import { useRegex } from "../../../utils/useRegex";
import { oids } from "@/configs/Settings";
import { normalizeMigrationCpfToCnpj } from "../../../services/fhir/organization/normalizeMigrationCpfToCnpj";
import { summarizeOrganizationChanges } from "./changeToCnpj/organizationChangeSummary";
import { ReviewChangeToCnpj } from "./changeToCnpj/ReviewChangeToCnpj";
import { normalizeAltToFhirOrganization } from "../../../services/fhir/organization/normalizeAltToFhirOrganization";

interface IProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

type FormValues = {
  cnpjValue: string;
};

function onlyDigits(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value).replace(/\D/g, "");
}

function isOrganization(value: any): value is fhir5.Organization {
  return (
    value && typeof value === "object" && value.resourceType === "Organization"
  );
}

type AltSocio = { cpf?: unknown; nome?: unknown };

function findMatchedSocioName(
  socios: unknown,
  userCpfDigits: string,
): string | null {
  if (!userCpfDigits) return null;

  // Alguns retornos trazem `socios` como objeto, outros como lista.
  if (Array.isArray(socios)) {
    const match = (socios as AltSocio[]).find(
      (s) => onlyDigits(s?.cpf) === userCpfDigits,
    );
    return match?.nome ? String(match.nome) : null;
  }

  if (socios && typeof socios === "object") {
    const s = socios as AltSocio;
    if (onlyDigits(s?.cpf) !== userCpfDigits) return null;
    return s?.nome ? String(s.nome) : null;
  }

  return null;
}

export function ChangeToCnpjDialog({ isOpen, setIsOpen }: IProps) {
  const { user } = useSessionContext();
  const { organization, refetchOrganization } = useOrganizationContext();

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<0 | 1>(0);
  const [cnpjDigits, setCnpjDigits] = useState<string>("");
  const [validatedOrganization, setValidatedOrganization] =
    useState<fhir5.Organization | null>(null);

  const methods = useForm<FormValues>({ defaultValues: { cnpjValue: "" } });
  const { control, handleSubmit, reset } = methods;

  const handleClose = () => {
    setIsOpen(false);
    setLoading(false);
    setStep(0);
    setCnpjDigits("");
    setValidatedOrganization(null);
    reset({ cnpjValue: "" });
  };

  const reviewData = useMemo(() => {
    return {
      cnpjDigits,
      cpfDigits: onlyDigits(user?.username),
      organizationName: organization?.name || "",
    };
  }, [cnpjDigits, organization?.name, user?.username]);

  const reviewPayload = useMemo(() => {
    if (!organization || !cnpjDigits) return null;
    try {
      const after = normalizeMigrationCpfToCnpj({
        currentOrganization: organization,
        newCnpjDigits: cnpjDigits,
        validatedOrganization,
      });
      const changes = summarizeOrganizationChanges({
        before: organization,
        after,
      });
      return { after, changes };
    } catch {
      return null;
    }
  }, [cnpjDigits, organization, validatedOrganization]);

  const verifyAlreadyHasCnpjOrganization = async (
    cnpjDigitsToCheck: string,
  ) => {
    try {
      const response = await GetOrganizations({
        orgIds: `${oids.cnpj}-${cnpjDigitsToCheck}`,
        active: true,
      });

      if (isOrganization(response)) {
        return true;
      }

      return false;
    } catch (err) {
      console.log("err", err);
      return false;
    }
    // Critério mínimo e consistente com o backend:
    // - se encontrar qualquer organização prov diferente da atual, interrompe o fluxo
  };

  const onValidateAndGoReview = async (data: FormValues) => {
    if (!organization?.id) {
      toast.error(
        "Organização atual não encontrada. Atualize a página e tente novamente.",
      );
      return;
    }

    const digits = onlyDigits(data?.cnpjValue);
    if (!digits) return;

    setLoading(true);
    try {
      const alreadyHas = await verifyAlreadyHasCnpjOrganization(digits);
      if (alreadyHas) {
        toast.warn(
          "Já existe uma organização cadastrada com CNPJ. Não é possível prosseguir com a migração.",
        );
        handleClose();
        return;
      }

      const validation = await Validate({
        cpf: user.username,
        cnpj: digits,
      });

      if (validation?.status === HTTPResponseStatus.Conflict) {
        toast.warn(validation?.data);
        return;
      }
      if (validation?.status !== HTTPResponseStatus.Created) {
        toast.error(validation?.data);
        return;
      }

      // Resetamos o “retorno tratado” antes de recalcular.
      setValidatedOrganization(null);

      const validationData: unknown = validation?.data;
      if (!isOrganization(validationData)) {
        // Fallback de segurança: quando o backend retorna payload alternativo,
        // validamos no frontend se o CPF logado consta como sócio do CNPJ.
        const userCpfDigits = onlyDigits(user?.username);
        const alt = validationData as { socios?: unknown } | null | undefined;
        const matchedSocioName = findMatchedSocioName(
          alt?.socios,
          userCpfDigits,
        );
        if (!matchedSocioName) {
          toast.error(
            "O CPF do usuário não coincide com nenhum sócio do CNPJ inserido",
          );
          return;
        }

        // `Validate` pode retornar payload "alt" (não-FHIR). Normalizamos para FHIR
        // para preencher campos ausentes (qualification/contato/alias) e exibir “adicionados”.
        const normalized = normalizeAltToFhirOrganization({
          alt: validationData,
          matchedSocioName,
          creatorCpf: user.username,
        });
        setValidatedOrganization(normalized);
      } else {
        // Quando o backend retorna FHIR, guardamos para merge/diff.
        setValidatedOrganization(validationData);
      }

      // Segurança: se por algum motivo não conseguimos “tratar” o retorno do Validate,
      // não deixamos avançar para Review/PUT.
      // (ex.: backend retornou payload inesperado)
      // Obs: setState é async; então validamos pelo próprio `validationData`.
      const willHaveValidated =
        isOrganization(validationData) ||
        (validationData && typeof validationData === "object");
      if (!willHaveValidated) {
        toast.error(
          "Retorno de validação inválido. Não é possível prosseguir.",
        );
        return;
      }

      setCnpjDigits(digits);
      setStep(1);
    } catch (err) {
      console.log("err", err);
      toast.error("Não foi possível validar o CNPJ. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const onConfirmPatch = async () => {
    if (!organization?.id) return;
    if (!cnpjDigits) return;

    setLoading(true);
    try {
      if (!validatedOrganization) {
        toast.error(
          "Validação obrigatória não encontrada. Volte e valide o CNPJ novamente.",
        );
        setStep(0);
        return;
      }

      if (!reviewPayload?.after) {
        toast.error("Não foi possível preparar os dados para atualização.");
        return;
      }

      await PatchOrganizationCpfToCnpj({
        organization,
        newCnpjDigits: cnpjDigits,
        validatedOrganization,
      });

      toast.success("Cadastro alterado para CNPJ com sucesso!");
      refetchOrganization();
      handleClose();
    } catch (err) {
      console.log("err", err);
      toast.error("Não foi possível atualizar a organização. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <CommonDialog handleClose={handleClose} isOpen={isOpen}>
      <DialogTitle textAlign="center">Alterar cadastro para CNPJ</DialogTitle>
      <DialogContent>
        {step === 0 && (
          <Stack spacing={3} width="100%" alignItems="center">
            <Typography
              variant="body1"
              color="neutral700.main"
              textAlign="center"
            >
              Insira o CNPJ da sua organização de saúde para habilitar convites
              de profissionais.
            </Typography>

            <FormTextInput
              name="cnpjValue"
              control={control}
              label="CNPJ"
              required
              pattern={useRegex.cnpj}
              mask={useFormatter.formatCNPJ}
              autoFocus
            />

            <Stack width="100%" mt={1}>
              <FormButtonGroup
                onGoBackButtonClick={handleClose}
                onNextButtonClick={handleSubmit(onValidateAndGoReview)}
                goBackButtonText="Cancelar"
                nextButtonText="Validar"
                loading={loading}
              />
            </Stack>
          </Stack>
        )}

        {step === 1 && (
          <>
            {reviewPayload?.after && (
              <ReviewChangeToCnpj
                cpfValue={user.username}
                organizationAfter={reviewPayload.after}
                loading={loading}
                onBack={() => setStep(0)}
                onConfirm={onConfirmPatch}
              />
            )}
          </>
        )}
      </DialogContent>
    </CommonDialog>
  );
}
